import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getSystemConfig, updateMFAPolicy } from '../../services/systemConfigService';
import { SystemConfig } from '../../types/systemConfig';
import toast from 'react-hot-toast';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';

const MFAPolicySettings: React.FC = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const systemConfig = await getSystemConfig();
      if (systemConfig) {
        setConfig(systemConfig);
        setMfaRequired(systemConfig.mfaPolicy.required);
      }
    } catch (error) {
      console.error('Error loading system config:', error);
      toast.error('Hiba a konfiguráció betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.id) return;

    try {
      setSaving(true);

      await updateMFAPolicy(
        {
          required: mfaRequired,
          gracePeriodDays: 0
        },
        user.id
      );

      toast.success('MFA szabályzat sikeresen mentve!', {
        icon: '✅',
      });

      await loadConfig();
    } catch (error) {
      console.error('Error saving MFA policy:', error);
      toast.error('Hiba a mentés során');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-pink-600 rounded-2xl p-6 text-white">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-white/20 rounded-xl">
            <ShieldCheckIcon className="h-8 w-8" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">MFA Szabályzat</h2>
            <p className="text-red-100 mt-1">
              Rendszer szintű kétfaktoros hitelesítés beállítások
            </p>
          </div>
        </div>
      </div>

      {/* Settings Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-6 space-y-6">
          {/* MFA Required Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <div className="flex items-start space-x-3">
              <ShieldCheckIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  MFA kötelező minden felhasználónak
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Ha bekapcsolod, minden felhasználónak be kell kapcsolnia a kétfaktoros hitelesítést.
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={mfaRequired}
                onChange={(e) => setMfaRequired(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-500/30"
            >
              {saving ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Mentés...</span>
                </div>
              ) : (
                'Szabályzat mentése'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAPolicySettings;
